import { checklistItems } from "@/components/ProjectExtraTabs";

const ORDER = ["Lead", "Quoted", "Approved", "Template", "Fabrication", "Install", "Complete"];
const rank = (s) => ORDER.indexOf(s);
const atLeast = (stage, min) => rank(stage) >= rank(min);
const LEVEL_RANK = { block: 0, warn: 1, info: 2 };

const STOPS = [
  ["Template", "template_date", "template_crew"],
  ["Fabrication", "fab_date", "fab_crew"],
  ["Install", "install_date", "install_crew"],
];

const money = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const shortDay = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
const daysUntil = (iso, today) => Math.round((Date.parse(iso) - Date.parse(today)) / 86400000);

export const projectFacts = ({ p, invoices = [], conflicts = [], today }) => {
  const todayIso = today || new Date().toISOString().slice(0, 10);
  const jobs = p.jobs || [];
  const quotes = p.quotes || [];
  const fixtures = p.fixtures || [];
  const tasks = p.tasks || [];
  const specials = p.special_orders || [];
  const accepted = quotes.filter((q) => q.status === "Accepted");
  const dealQuote = accepted[0] || quotes[0] || null;
  const templateJobs = jobs.filter((j) => j.template_date);
  const installJobs = jobs.filter((j) => j.install_date);
  const nextStop = jobs
    .flatMap((j) =>
      STOPS.map(([label, dateKey, crewKey]) => ({
        job: j,
        label,
        date: j[dateKey],
        crew: j[crewKey],
      })),
    )
    .filter((s) => s.date && s.date >= todayIso)
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))[0];
  const conflictIds = new Set(
    conflicts.flatMap((c) => (c.stops || []).map((s) => s.job_id)),
  );
  const live = invoices.filter((i) => i.status !== "Void");
  const openInvoices = live.filter((i) => (i.balance || 0) > 0.009);
  const deposit = live.find((i) => i.kind === "deposit") || null;
  const supplied = fixtures.filter((f) => f.in_stock === false && !f.confirmed);
  return {
    todayIso,
    jobs,
    quotes,
    fixtures,
    tasks,
    accepted,
    dealQuote,
    templateJobs,
    installJobs,
    nextStop,
    projectConflicts: jobs.filter((j) => conflictIds.has(j.id)),
    openInvoices,
    deposit,
    depositOpen: !!deposit && (deposit.balance || 0) > 0.009,
    overdueInvoices: openInvoices.filter(
      (i) => i.kind !== "deposit" && i.due_date && i.due_date.slice(0, 10) < todayIso,
    ),
    overdueTasks: tasks.filter((t) => !t.done && t.date && t.date < todayIso),
    openTasks: tasks.filter((t) => !t.done),
    unconfirmed: fixtures.filter((f) => !f.confirmed),
    supplied,
    pendingSpecial: specials.filter((s) => (s.status || "Ordered") !== "Received"),
    attentionSpecial: specials.filter((s) => s.status === "Attention"),
    openChecklist: checklistItems(p).filter((i) => !i.done),
    materials: p.materials || [],
  };
};

// Blockers + nudges for the project, worst first. `go` tells the page where to jump.
export const nextSteps = (ctx) => {
  const f = projectFacts(ctx);
  const { p } = ctx;
  const stage = p.stage;
  const closed = stage === "Complete" || stage === "Lost";
  const out = [];
  const add = (key, level, text, go, cta) => out.push({ key, level, text, go, cta });

  if (!p.customer_id)
    add("account", "warn", "No account linked — texts, invoices and the portal need one.", { action: "account" }, "Accounts");
  else if (!p.customer_phone)
    add("phone", "warn", "Customer has no phone on file — appointment texts can't go out.", { action: "customer" }, "Open account");

  if (f.quotes.length === 0 && !closed)
    add("quote", "block", "No quote yet — start one to price the job.", { action: "newQuote" }, "New quote");
  else if (f.quotes.length > 0 && f.accepted.length === 0 && stage !== "Lost")
    add("accept", atLeast(stage, "Approved") ? "block" : "warn",
      `Quote ${f.dealQuote.code} isn't accepted by the customer yet.`, { tab: "work" }, "Sign off");

  // Drawing sign-off was retired: drawings no longer gate the project.

  if (f.jobs.length === 0 && atLeast(stage, "Approved") && !closed)
    add("job", "block", "No job on this project — create one to book template and install.", { action: "newJob" }, "New job");
  if (f.jobs.length > 0 && !closed) {
    if (f.templateJobs.length === 0 && atLeast(stage, "Approved") && !atLeast(stage, "Fabrication"))
      add("template", "block", "Template isn't booked.", { tab: "schedule" }, "Schedule");
    if (f.installJobs.length === 0 && atLeast(stage, "Template"))
      add("install", atLeast(stage, "Install") ? "block" : "warn", "Install date isn't set.", { tab: "schedule" }, "Schedule");
  }

  if (f.nextStop && !f.nextStop.crew)
    add("crew", "warn", `${f.nextStop.label} on ${shortDay(f.nextStop.date)} has no crew assigned.`, { tab: "schedule" }, "Assign crew");
  if (f.nextStop && !f.nextStop.job.appt_confirmed_at && daysUntil(f.nextStop.date, f.todayIso) <= 3)
    add("confirm", "warn", `${f.nextStop.label} on ${shortDay(f.nextStop.date)} isn't confirmed by the customer.`, { tab: "schedule" }, "Text appt");
  if (f.projectConflicts.length)
    add("conflict", "block", `Crew double-booked on ${f.projectConflicts.map((j) => j.code).join(", ")}.`, { tab: "schedule" }, "Fix schedule");

  // Sink + cutout confirmation nags removed by request.
  if (f.fixtures.length === 0 && (stage === "Template" || stage === "Fabrication"))
    add("nofixtures", "info", "No sink or cutout fixtures recorded yet.", { tab: "fixtures" }, "Add fixture");

  if (f.materials.length === 0 && atLeast(stage, "Approved") && !closed)
    add("slab", atLeast(stage, "Fabrication") ? "block" : "warn", "No slab reserved to this project yet.", { action: "slabs" }, "Pick slab");

  if (f.attentionSpecial.length)
    add("special-attn", "block",
      `${plural(f.attentionSpecial.length, "special order")} need${f.attentionSpecial.length === 1 ? "s" : ""} attention (${f.attentionSpecial.map((s) => s.item || "item").join(", ")}).`,
      { tab: "special" }, "Special orders");
  else if (f.pendingSpecial.length && atLeast(stage, "Template") && !closed)
    add("special", "warn", `${plural(f.pendingSpecial.length, "special order")} not received yet.`, { tab: "special" }, "Special orders");

  if (f.overdueTasks.length)
    add("tasks", "block",
      `${plural(f.overdueTasks.length, "overdue task")}: ${f.overdueTasks.map((t) => t.label || t.title || t.kind).filter(Boolean).slice(0, 3).join(", ")}.`,
      { tab: "tasks" }, "Tasks");

  if (f.depositOpen)
    add("deposit", atLeast(stage, "Template") ? "block" : "warn",
      `Deposit ${f.deposit.code} unpaid — ${money(f.deposit.balance)}.`, { action: "invoice", id: f.deposit.id }, "Open invoice");
  f.overdueInvoices.forEach((i) =>
    add(`inv-${i.id}`, "warn", `Invoice ${i.code} overdue — ${money(i.balance)}.`, { action: "invoice", id: i.id }, "Open invoice"),
  );
  if (stage === "Complete" && f.openInvoices.length)
    add("balance", "warn",
      `${money(f.openInvoices.reduce((n, i) => n + (i.balance || 0), 0))} still outstanding after install.`,
      { action: "invoice", id: f.openInvoices[0].id }, "Collect");

  if (f.openChecklist.length && atLeast(stage, "Approved") && !closed)
    add("checklist", "info", `${plural(f.openChecklist.length, "checklist item")} still open.`, { tab: "checklist" }, "Checklist");
  if (p.unread_notes > 0)
    add("unread", "warn", `${plural(p.unread_notes, "unread customer message")}.`, { tab: "comms" }, "Reply");
  if (p.hold_status) {
    const d = p.hold_since ? Math.floor((Date.now() - Date.parse(p.hold_since)) / 86400000) : 0;
    const fu = p.hold_follow_up;
    const overdue = fu && fu <= f.todayIso;
    add("hold", overdue ? "block" : "warn",
      `${p.hold_status}${d > 0 ? ` for ${d}d` : ""}${p.hold_note ? ` — ${p.hold_note}` : ""}${fu ? ` · follow up ${shortDay(fu)}` : ""}.`,
      { action: "hold" }, overdue ? "Follow up now" : "Clear hold");
  }

  return out.sort((a, b) => LEVEL_RANK[a.level] - LEVEL_RANK[b.level]);
};

// What should be true before moving the project into `target`.
export const advanceChecks = (target, ctx) => {
  const f = projectFacts(ctx);
  const checks = [];
  const c = (ok, text) => checks.push({ ok: !!ok, text });
  const done = f.fixtures.length - f.unconfirmed.length;
  switch (target) {
    case "Quoted":
      c(f.quotes.length > 0, "A quote exists");
      break;
    case "Approved":
      c(!!ctx.p.customer_id, "Account linked");
      c(f.accepted.length > 0, "Quote accepted by the customer");
      break;
    case "Template":
      c(f.accepted.length > 0, "Quote accepted");
      c(f.templateJobs.length > 0, "Template date booked");
      c(!!ctx.p.customer_phone, "Customer phone on file");
      break;
    case "Fabrication":
      c(f.fixtures.length > 0 && f.unconfirmed.length === 0, `All cutouts confirmed (${done}/${f.fixtures.length})`);
      c(f.materials.length > 0, "Slab reserved");
      c(f.pendingSpecial.length === 0, "Special orders received");
      break;
    case "Install":
      c(f.installJobs.length > 0, "Install date set");
      c(f.installJobs.some((j) => j.install_crew), "Install crew assigned");
      c(!f.depositOpen, "Deposit paid");
      c(f.projectConflicts.length === 0, "No crew double-bookings");
      break;
    case "Complete":
      c(f.openInvoices.length === 0, "Balance collected");
      c(f.openChecklist.length === 0, "Checklist complete");
      c(f.openTasks.length === 0, "All tasks done");
      break;
    default:
      break;
  }
  return checks;
};

// Badge counts for the tab bar: { key: { n, tone } }.
export const tabCounts = (p, conflicts = []) => {
  const f = projectFacts({ p, conflicts });
  const out = {};
  const set = (key, n, tone = "indigo") => {
    if (n > 0) out[key] = { n, tone };
  };
  set("tasks", f.openTasks.length, f.overdueTasks.length ? "red" : "indigo");
  set("checklist", f.openChecklist.length);
  set("fixtures", f.unconfirmed.length, "amber");
  set("special", f.pendingSpecial.length, f.attentionSpecial.length ? "red" : "amber");
  set("schedule", f.projectConflicts.length, "red");
  set("comms", p.unread_notes || 0);
  return out;
};
